import { html, internalProperty, property } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { nothing } from 'lit-html';
import {
  KatLitElement,
  Keys,
  register,
  nextUniqueId,
  event,
  EventEmitter,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';
import {
  formatDateObject,
  isValidDateObject,
  getDateFormatFromLocale,
  createDateFromStringAndFormat,
} from '../../shared/base/date-utils';
import strings from '../date-picker/strings';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './date-picker.lit.scss';

/**
 * @component {kat-date-picker} KatalDatepicker Allows a user to select a date and provides a display for the selected date.
 * @example UnitedStatesLocale {"locale": "en-US", "label": "US English", "value": ""}
 * @example Disabled {"locale": "en-US", "label": "Disabled", "value": "", "disabled": "true"}
 * @example GermanLocale {"locale": "de-DE", "label": "German", "value": "01.01.2018"}
 * @example ChineseLocale {"locale": "zh-CN", "label": "Mandarin", "value": "2019/01/01", "script": ""}
 * @example IsoFormat {"locale": "en-US", "label": "Set by ISO String", "value": "2019-09-21T12:00:00.000Z", "script": ""}
 * @example DisabledWeekends {"locale": "en-US", "label": "Disabled Weekends",
 * "script": "
 *   document.querySelector(\"kat-date-picker\").isDateDisabled = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 * "}
 * @example ColorCodedDates {"locale": "en-US", "label": "Color coded dates",
 * "script": "
 *   var SCARY_DAY_OF_MONTH = 13;
 *   document.querySelector(\"kat-date-picker\").getDateDecorationConfig = function (date) {
 *     if (date.getDate() === SCARY_DAY_OF_MONTH) {
 *         return {style: 'color-code-01', ariaLabel: 'Do not book today.'};
 *     }
 *     return null;
 *   };
 * "}
 * @example WithTooltip {"locale": "en-US", "label": "Date picker with tooltip", "value": "",
 * "tooltip-text": "Hello world"}
 * @status Production
 * @theme flo
 */
@register('kat-date-picker')
export class KatDatePicker extends KatLitElement {
  /**
   * Set the value for the date picker. This will only emit an 'invalid' date if it is not well-formed. No 'change' event is
   * emitted if successfully set
   * @type {string | Date}
   */
  @property()
  get value(): string {
    return this._value || '';
  }

  set value(value: string) {
    const oldValue = this.value;
    this.rawValue = value;
    if (!this.locale) return;

    if (
      value == null ||
      value === '' ||
      !(
        value instanceof Date ||
        typeof value === 'string' ||
        typeof value === 'number'
      )
    ) {
      this.updateValueAndDate(null, null);
      return;
    }

    if (typeof value === 'string') {
      // Cast to integer if is number
      if (value.match(/^[0-9]+$/)) {
        value = new Date(parseInt(value, 10));
      } else if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
        value = new Date(value);
      }
    } else if (typeof value === 'number') {
      value = new Date(value);
    }

    let dateObject = value;
    let formattedValue = value;

    // Try converting to format specified by locale
    if (value instanceof Date) {
      formattedValue = formatDateObject(value, this.placeholder);
    } else {
      dateObject = createDateFromStringAndFormat(value, this.placeholder);
    }

    if (dateObject && this.isDateDisabled && this.isDateDisabled(dateObject)) {
      this.focus();
      return;
    }

    if (!isValidDateObject(dateObject)) {
      this._invalid.emit({ value });
      this.updateValueAndDate(null, null);
      return;
    }

    this.updateValueAndDate(formattedValue, dateObject);
    this.requestUpdate('value', oldValue);
  }

  /**
   *  The current value as a standard Date object. Setting this attribute/property
   * is exactly the same as setting "value". The "date-value" attribute will not
   * be kept up-to-date automatically.
   * @type {string | Date}
   */
  @property({ type: String, reflect: false })
  get dateValue(): string {
    return this._dateObject;
  }

  set dateValue(value: string) {
    this.value = value;
  }

  @internalProperty()
  _dateObject: Date = null;

  @internalProperty()
  showing = false;

  @internalProperty()
  focused = false;

  /** The locale the calendar should be using */
  @property()
  get locale(): string {
    return this._locale;
  }

  set locale(locale: string) {
    const oldLocale = this._locale;
    this._locale = locale;
    if (this.rawValue && !this.value) {
      this.value = this.rawValue;
    } else if (this.value?.length && oldLocale && locale) {
      const oldFormatString = getDateFormatFromLocale(oldLocale);
      const newFormatString = getDateFormatFromLocale(locale);
      const currentDate = createDateFromStringAndFormat(
        this.value,
        oldFormatString
      );
      this.value = formatDateObject(currentDate, newFormatString);
    }
    this.requestUpdate('locale', oldLocale);
  }

  /** Set the Label for the input */
  @property()
  label?: string;

  /** Set the name attribute for the input */
  @property()
  name?: string;

  /** If true the datepicker will be disabled and unable to be interacted with. */
  @property()
  disabled?: boolean;

  /**
   * Sets the function used to determine which dates are disabled. Called for each date rendered in the
   * date picker to determine if the date should be disabled. If this function returns true the given date will be disabled. If the logic of this function is
   * changed the disabled state will only be displayed after the next time the user opens the date picker calendar. If instead you use this setter to set the
   * function again the calendar will be re-rendered immediately with the new logic.
   */
  @property({ attribute: false })
  isDateDisabled?: (date: Date) => boolean;

  /**
   * Used to define a function that is called each time a day is rendered.
   * Passed the date the day represents.
   * Returns a configuration object that contains the date rendering style and accessibility content
   * or null if no specific styling is applicable to the date.
   * style attribute must be one of "color-code-01" | "color-code-02" | "color-code-03" | "color-code-04" | "color-code-05" |
   * "color-code-06" | "color-code-07" | "color-code-08" | "color-code-09" | "color-code-10".
   *
   * Each style must be accompanied with a corresponding accessibility blurb that describes the specifics of the date.
   *
   * E.G. {style: 'color-code-01', ariaLabel: 'Bookings today require payment.'}
   */
  @property({ attribute: false })
  getDateDecorationConfig?: (
    date: Date
  ) => { style: string; ariaLabel: string } | null;

  /**
   * Specifies the autocomplete behaviour that a browser should attempt:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @property()
  autocomplete = 'off';

  /** The label used exclusively by screenreaders as a fallback if the regular label is not specified. */
  @property({ attribute: 'kat-aria-label' })
  katAriaLabel?: string;

  /**
   * The text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  @property({ attribute: 'tooltip-text' })
  tooltipText?: string;

  /** The position of the tooltip. Defaults to "top". */
  @property({ attribute: 'tooltip-position' })
  tooltipPosition?: string;

  /** The icon that triggers the tooltip next to the label. Defaults to "help_outline". */
  @property({ attribute: 'tooltip-trigger-icon' })
  tooltipTriggerIcon?: string;

  static get styles() {
    return [baseStyles, styles];
  }

  _propsToReflectToHiddenInput = ['name', 'value', 'disabled'];

  /** Fired when either date is updated. */
  @event('change', true)
  private _change: EventEmitter<{
    value: string | Date; // TODO: this should not be one or the other
    isoValue: string;
    dateObject: Date;
  }>;

  /** Fired when the entered value is not a valid Date. The invalid value will be attached to the event as event.detail.value. */
  @event('invalid', true)
  private _invalid: EventEmitter<{ value: string }>;

  constructor() {
    super();

    this._uniqueLabelId = nextUniqueId();

    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('click', this._onClick);
    this.addEventListener('focusout', this._onFocusOut);
  }

  firstUpdated() {
    if (!this.locale) {
      this.locale = 'en-US';
    }
  }

  updateValueAndDate(val, datObj) {
    // In the context of "set value", these three should be set together
    this._dateObject = datObj;
    this._value = val;
    this.lastValue = this._value;
  }

  get placeholder() {
    return getDateFormatFromLocale(this.locale);
  }

  show() {
    if (this.showing || this.disabled) {
      return;
    }
    this.showing = true;
  }

  hide() {
    if (this.static || !this.showing) {
      return;
    }
    this.showing = false;
  }

  // Called when a user interaction changes the selected date
  emitChangeEvent(value) {
    if (value === this.lastValue) {
      return;
    }

    // Setting value sets this.lastValue
    this.value = value;

    const isoValue = this._dateObject ? this._dateObject.toISOString() : '';
    this._change.emit({
      value,
      isoValue,
      dateObject: this._dateObject,
    });
  }

  render() {
    const calendarClasses = { show: this.showing };
    const groupClasses = { input__container: true, focused: this.focused };
    const label = this.label
      ? html`<kat-label
          for="${this._uniqueLabelId}"
          class="date-label"
          part="date-picker-label"
          text=${this.label}
          .tooltipText=${ifNotNull(this.tooltipText)}
          .tooltipPosition=${ifNotNull(this.tooltipPosition)}
          .tooltipTriggerIcon=${ifNotNull(this.tooltipTriggerIcon)}
        ></kat-label>`
      : nothing;

    const ariaLabel = this.katAriaLabel ? this.katAriaLabel : this.label;

    return html`<div class="container">
        ${label}
        <div class=${classMap(groupClasses)}>
          <kat-icon
            name="calendar-alt"
            part="date-picker-icon"
            class="icon"
            role="button"
            tabindex="-1"
            aria-label="${this.getAriaLabel()}"
            aria-expanded=${this.showing}
            @click=${this.show}
          ></kat-icon>
          <kat-input
            type="text"
            part="date-picker-input"
            class="input"
            placeholder=${ifNotNull(this.placeholder)}
            name=${ifNotNull(this.name)}
            value=${ifNotNull(this.value)}
            autocomplete=${ifNotNull(this.autocomplete)}
            ?disabled=${this.disabled}
            kat-aria-label=${ariaLabel}
            unique-id=${this._uniqueLabelId}
            @change=${this._onInputChange}
            @keydown=${this._onInputKey}
            @click=${this._onClick}
          ></kat-input>
        </div>
      </div>
      <kat-calendar
        tabindex="0"
        part="date-picker-calendar"
        class=${classMap(calendarClasses)}
        locale=${ifNotNull(this.locale)}
        .isDateDisabled=${this.isDateDisabled}
        .getDateDecorationConfig=${this.getDateDecorationConfig}
        .value=${this._dateObject}
        @hide=${this._onCalendarHide}
        @change=${this._onCalendarChange}
        @focusin=${this._onCalendarFocus}
        @fullblur=${this._onCalendarBlur}
      ></kat-calendar>
      <slot name="private-light-dom"></slot>`;
  }

  _onClick() {
    this.focus();

    this.show();
  }

  _onInputChange(e) {
    e.stopImmediatePropagation();
    this.emitChangeEvent(e.target.value);
  }

  _onFocusIn() {
    this.focused = true;
  }

  _onFocusOut(e) {
    e.stopImmediatePropagation();
    const relatedTarget = e.relatedTarget;

    const relatedTargetInsideComponent =
      relatedTarget && this.shadowRoot.contains(relatedTarget);

    if (!relatedTargetInsideComponent) {
      this.hide();
      this.focused = false;
    }
  }

  _onCalendarChange(e) {
    e.stopImmediatePropagation();

    // Receive the date event and render it using the date format
    const formattedDate = formatDateObject(e.detail.value, this.placeholder);

    this.emitChangeEvent(formattedDate);
    this.hide();

    // This is necessary because change can be invoked when the enter key is pressed and focusing
    // the input while this event is being processed will cause the keypressed event to fire for
    // the input. Since we hook into the input pressing enter it causes the calendar to be shown.
    setTimeout(() => {
      this.focus();
    }, 0);
  }

  _onCalendarHide(e) {
    e.stopImmediatePropagation();

    this.focus();
  }

  _onCalendarFocus(e) {
    e.stopImmediatePropagation();
  }

  _onInputKey(e) {
    if (e.keyCode === Keys.Enter) {
      this.show();
    }
    if (e.keyCode === Keys.Escape) {
      this.hide();
    }
  }

  focus() {
    const input = this.shadowRoot.querySelector('kat-input');
    input.focus();
  }

  getAriaLabel() {
    const str = strings[this.locale];
    return str ? str : strings['en-US'];
  }
}
